/**
 * @file report-date-range.tsx
 * @module app/modules/uProcure/inventory-replenishment/report-date-range
 * @description Date range picker of the Inventory Replenishment report: quick ranges (last 7 / 30 / 90 / 180 days,
 *   1 year, this month, last month, this year), two months side by side with month / year
 *   dropdowns, at most 366 days and never after today.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DATE_PRESETS, daysInRange, matchingPreset, maxEndDate } from "./report-date-presets";

interface Props {
  start: Date | null;
  end: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
  disabled?: boolean;
}

const ReportDateRange = ({ start, end, onChange, disabled }: Props) => {
  const today = new Date();
  const active = matchingPreset(start, end, today);
  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            disabled={disabled}
            className={`btn btn-sm ${active === p.key ? "btn-primary" : "btn-light"}`}
            onClick={() => {
              const r = p.range(today);
              onChange(r.start, r.end);
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="d-flex align-items-center gap-3">
        <DatePicker
          className="form-control bg-white fs-14 fw-600 text-dark h-50px border border-r8px"
          selectsRange
          startDate={start}
          endDate={end}
          onChange={(dates: [Date | null, Date | null]) => onChange(dates[0], dates[1])}
          maxDate={maxEndDate(start, end, today)}
          monthsShown={2}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          dateFormat="dd/MM/yyyy"
          placeholderText="Choose start and end date"
          isClearable
          disabled={disabled}
        />
        {start && end && <span className="fs-13 text-gray-700 text-nowrap">{daysInRange(start, end)} days</span>}
      </div>
    </div>
  );
};

export default ReportDateRange;
